import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import api from '@/lib/api';
import { giraffeRoot, giraffeTrip } from '@/lib/giraffePaths';
import { Pencil, Trash2, FileDown } from 'lucide-react';
import { useConfirmation } from '../components/shared/ZCard';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import OverflowMenu from '@/components/shared/OverflowMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiraffeMemoir() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const isAuthenticated = !!localStorage.getItem('werner_token');
  const feedback = useConfirmation();

  useEffect(() => {
    api.get(`/giraffe/memoir/${slug}`)
      .then(r => setTrip(r.data))
      .catch(err => setError(err.response?.status === 403 ? 'private' : 'notfound'));
  }, [slug]);

  const getImageUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  const toggleMemoirPublish = async () => {
    if (!trip) return;
    const tripId = trip.id || trip._id;
    const newVal = !trip.memoir_published;
    // Optimistic flip
    setTrip(prev => ({ ...prev, memoir_published: newVal }));
    try {
      await api.put(`/trips/${tripId}`, { memoir_published: newVal });
      feedback.confirm();
    } catch {
      setTrip(prev => ({ ...prev, memoir_published: !newVal }));
      feedback.fail();
    }
  };

  if (error === 'private') return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
      <div className="text-center">
        <h1 className="text-2xl text-[#f0f0f5] font-serif mb-2">Private Trip</h1>
        <p className="text-[#b0b4c0] font-light">This memoir is private.</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
      <div className="text-center">
        <h1 className="text-2xl text-[#f0f0f5] font-serif mb-2">Not Found</h1>
        <p className="text-[#b0b4c0] font-light">This memoir doesn't exist.</p>
      </div>
    </div>
  );

  if (!trip) return (
    <div className="min-h-screen bg-[#0a0a0f]" style={{ padding: '48px 24px' }}>
      <ZLoadingBar accent="#10B981" loading={true} />
    </div>
  );

  const memoirEntries = (trip.entries || []).filter(e => e.entry_type === 'memoir' || e.entry_type === 'fragment' || !e.entry_type);

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="memoir-page">
      {isAuthenticated && (
        <div style={{ height: 32, display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)', padding: '0 24px' }}>
          <Breadcrumb>
            <BreadcrumbList style={{ gap: 0 }}>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={giraffeRoot()} style={{ color: '#6b7084', fontSize: 13, fontWeight: 400, textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
                    onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
                    Giraffe
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={giraffeTrip(trip.id || trip._id)} style={{ color: '#6b7084', fontSize: 13, fontWeight: 400, textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
                    onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
                    {trip.title}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
              <BreadcrumbItem>
                <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontWeight: 400 }}>
                  Memoir
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      )}
      {/* Hero */}
      <div className="relative h-[60vh] flex items-end" style={{ position: 'relative' }}>
        {isAuthenticated ? (
          <OverflowMenu
            publishToggle={{ isPublished: !!trip.memoir_published, onToggle: toggleMemoirPublish }}
            items={[
              {
                icon: <FileDown size={16} strokeWidth={1.5} />,
                label: 'Download PDF',
                onClick: () => {
                  const base = window.location.hostname.startsWith('giraffe.') ? '' : '/giraffe';
                  window.open(`${base}/memoir/${slug}/print?auto=1`, '_blank', 'noopener,noreferrer');
                },
              },
            ]}
          />
        ) : (
          <OverflowMenu
            items={[
              {
                icon: <FileDown size={16} strokeWidth={1.5} />,
                label: 'Download PDF',
                onClick: () => {
                  const base = window.location.hostname.startsWith('giraffe.') ? '' : '/giraffe';
                  window.open(`${base}/memoir/${slug}/print?auto=1`, '_blank', 'noopener,noreferrer');
                },
              },
            ]}
          />
        )}
        {trip.cover_image ? (
          <img src={getImageUrl(trip.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[#6B35B8]/20 to-[#0a0a0f]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/40 to-transparent" />
        <div className="relative z-10 p-8 md:p-16 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-serif font-medium tracking-tight leading-none mb-4" data-testid="memoir-title">
            {trip.title}
          </h1>
          {trip.description && <p className="text-white/70 font-light text-base md:text-lg max-w-xl">{trip.description}</p>}
          {trip.start_date && (
            <p className="text-white/40 font-mono text-xs tracking-wider mt-4">
              {trip.start_date}{trip.end_date ? ` — ${trip.end_date}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Entries */}
      <div className="max-w-4xl mx-auto px-6 md:px-16 py-16 space-y-16">
        {memoirEntries.map((entry, i) => {
          if (entry.media_type === 'photo' || (entry.images && entry.images.length > 0) || entry.composed_image_url || entry.composed_url) {
            const isComposed = !!(entry.composed_image_url || entry.composed_url);
            const imgUrl = isComposed
              ? (entry.composed_image_url || entry.composed_url)
              : entry.images?.[0] ? getImageUrl(entry.images[0]) : null;
            return (
              <div key={entry.id || i} className="animate-fade-in">
                {imgUrl && (
                  <img src={imgUrl} alt={entry.title || ''} className="w-full rounded-xl object-cover max-h-[70vh]" />
                )}
                {!isComposed && (entry.title || entry.content) && (
                  <div className="mt-4">
                    {entry.title && <h3 className="text-xl text-[#f0f0f5] font-serif mb-1">{entry.title}</h3>}
                    {entry.content && <p className="text-[#b0b4c0] font-light leading-relaxed">{entry.content}</p>}
                  </div>
                )}
                {entry.location && <p className="text-[#b0b4c0]/70 text-xs font-mono mt-2">{entry.location}</p>}
              </div>
            );
          }

          if (entry.media_type === 'quote') {
            return (
              <div key={entry.id || i} className="py-8 animate-fade-in">
                <blockquote className="text-2xl md:text-3xl text-[#f0f0f5] font-light leading-relaxed italic font-serif max-w-2xl mx-auto text-center">
                  "{entry.quote_text || entry.content}"
                </blockquote>
                {entry.quote_author && (
                  <p className="text-[#9B6DD4] text-sm mt-4 font-mono tracking-wider text-center">-- {entry.quote_author}</p>
                )}
              </div>
            );
          }

          // Default: text entry
          return (
            <div key={entry.id || i} className="animate-fade-in">
              {entry.location && (
                <p className="text-[#b0b4c0]/70 text-xs font-mono tracking-[0.15em] uppercase mb-3">{entry.location}</p>
              )}
              {entry.title && <h3 className="text-2xl text-[#f0f0f5] font-serif mb-3">{entry.title}</h3>}
              <p className="text-[#f0f0f5]/80 font-light leading-relaxed text-base md:text-lg whitespace-pre-wrap">
                {entry.content}
              </p>
              <p className="text-[#b0b4c0]/60 text-xs font-mono mt-4">{entry.date}</p>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="text-center py-16">
        <p className="text-[#b0b4c0]/20 text-xs font-mono tracking-[0.3em]">WERNER LABS</p>
      </div>
    </div>
  );
}
